import React, { useEffect, useMemo, useState } from "react";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useEditCRFMutation } from "../dataAccess";
import { Autocomplete, createFilterOptions, Input } from "@mui/material";
import DisplayText from "../../../components/DisplayText/DisplayText";
import { CrfField } from "../nebula.service";
import { moveToNextCell } from "../utils/moveToNextCell";
import { StyledTooltip } from "./StyledTooltip";
import { afterDecimal } from "../utils/afterDecimal";

interface IEditableOptionsProps extends GridRenderEditCellParams {
    options: any[];
    fieldDefinition: CrfField;
    laterality: string;
    visitId: string;
    patientId: string;
    userEmail: string;
}

export const EditableOptions: React.FC<IEditableOptionsProps> = ({
    id,
    field,
    error,
    options,
    fieldDefinition,
    formattedValue = "",
    laterality,
    visitId,
    patientId,
    userEmail,
}) => {
    const isOptionSelected = options.map((option) => option.label).includes(formattedValue);
    const [inputValue, setInputValue] = React.useState(`${isOptionSelected ? "" : formattedValue}`);
    const apiRef = useGridApiContext();
    const [currentHighlightedOption, setCurrentHighlightedOption] = useState<{ label: string; value: any } | null>(
        null
    );
    const { enqueueSnackbar } = useSnackbar();
    const [editCRF] = useEditCRFMutation();

    const handleUpdateValueOnServer = async (newValue: string) => {
        const payload = {
            laterality,
            visitId,
            email: userEmail,
            data: [
                {
                    uuid: fieldDefinition.uuid,
                    fieldName: fieldDefinition.fieldName,
                    fieldValue: newValue,
                },
            ],
        };

        try {
            const isValid = await apiRef.current.setEditCellValue({ id, field, value: newValue });

            if (!isValid) return false;

            await apiRef.current.stopCellEditMode({
                id,
                field,
            });

            // @ts-ignore
            await editCRF({ patientId, crfUpdate: payload }).unwrap();
        } catch (e) {
            enqueueSnackbar("Something went wrong.", { variant: "error" });
            await apiRef.current.startCellEditMode({ id, field });
        }
    };

    // Apply new value, send update to the server and then move to next cell
    const handleKeyDown = async (event: any) => {
        switch (event.key) {
            case "Enter":
            case "Tab": {
                event.preventDefault();
                event.stopPropagation();

                const isValid = await apiRef.current.setEditCellValue({
                    id,
                    field,
                    value: currentHighlightedOption!.value,
                });

                if (isValid) {
                    handleUpdateValueOnServer(currentHighlightedOption!.value);

                    // move to next cell, timeout is necessary to wait until cell update is finished
                    setTimeout(() => moveToNextCell({ id, field }, apiRef, event), 0);
                }

                break;
            }
            default:
        }
    };

    const filter = createFilterOptions<any>();

    const freeInputOption = useMemo(() => {
        return options.find((option) => option.type === "input");
    }, [options]);

    const [dynamicOption, setDynamicOption] = useState<any | null>(null);

    const selectableOptions = useMemo(() => {
        const fixedOptions = options.filter((option) => option.type !== "input");

        if (freeInputOption && !dynamicOption) {
            const precisionHint = fieldDefinition.precision ? `x.${"x".repeat(fieldDefinition.precision)}` : "";
            fixedOptions.unshift({
                label: `Type a value ${fieldDefinition.unit ? `in ${fieldDefinition.unit}` : ""} ${
                    precisionHint ? `(${precisionHint})` : ""
                }`,
                disabled: true,
            });
        }

        return fixedOptions;
    }, [options, freeInputOption, dynamicOption]);

    useEffect(() => {
        if (freeInputOption && inputValue !== "" && !options.some((option) => inputValue === option.label)) {
            setDynamicOption({
                value: inputValue,
                label: `${inputValue} ${fieldDefinition.unit ?? ""}`.trim(),
                inputValue,
                type: "input",
            });
        } else {
            setDynamicOption(null);
        }
    }, [inputValue, options, fieldDefinition.unit, freeInputOption]);

    const displayedOptions = useMemo(() => {
        let opts = [...selectableOptions];
        if (dynamicOption) {
            opts.push(dynamicOption);
        }
        return opts;
    }, [selectableOptions, dynamicOption]);

    return (
        <StyledTooltip placement="top-start" open={!!error} title={error}>
            <Autocomplete
                sx={{ width: "100%" }}
                id="size-small-outlined"
                size="small"
                openOnFocus
                clearOnBlur
                autoHighlight
                inputValue={inputValue}
                onInputChange={(event, value, reason) => {
                    if (event && event.type === "blur") {
                        setInputValue("");
                    } else if (reason !== "reset") {
                        const isNumber = !isNaN(parseFloat(value));

                        if (!isNumber) {
                            return setInputValue(value);
                        }

                        if (
                            fieldDefinition.precision !== undefined &&
                            afterDecimal(parseFloat(value)) > fieldDefinition.precision
                        ) {
                            return;
                        }

                        setInputValue(value);
                    }
                }}
                options={displayedOptions}
                getOptionDisabled={(option) => !!option.disabled}
                onChange={async (event, newInputValue) => {
                    const isValid = await apiRef.current.setEditCellValue({ id, field, value: newInputValue.value });

                    if (isValid) {
                        handleUpdateValueOnServer(newInputValue.value);
                    }
                }}
                getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    if (option.inputValue) return String(option.inputValue);
                    return String(option.label);
                }}
                renderOption={(props, option) => (
                    <li {...props}>
                        <DisplayText type="bodySmall" text={option.label} />
                    </li>
                )}
                onHighlightChange={(_, option) => {
                    setCurrentHighlightedOption(option);
                }}
                renderInput={({ InputLabelProps, ...params }) => {
                    params.inputProps.onKeyDown = handleKeyDown;

                    return (
                        <Input
                            {...params}
                            autoFocus
                            disableUnderline
                            sx={{ p: 6 }}
                            ref={params.InputProps.ref}
                            fullWidth
                        />
                    );
                }}
            />
        </StyledTooltip>
    );
};
